import type { BehaviorSubject } from 'rxjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowPointer, faHand } from '@fortawesome/free-solid-svg-icons';
import useEventListener from '@use-it/event-listener';
import { useState } from 'react';
import { useObservableState } from 'observable-hooks';
import { CursorFunction } from '../../../graph/cursor-functions';
import { GraphToolbarButton } from './GraphToolbarButton';

const iconMap = {
    [CursorFunction.SELECT]: faArrowPointer,
    [CursorFunction.ZOOM]: faHand,
};

const ariaTextMap = {
    [CursorFunction.SELECT]: gettext('CURSOR_FUNCTIONS_SELECT'),
    [CursorFunction.ZOOM]: gettext('CURSOR_FUNCTIONS_ZOOM'),
};

const SHIFT = 'Shift';

interface Props {
    cursorFunction$: BehaviorSubject<CursorFunction>;
}

export function CursorFunctionSelector({ cursorFunction$ }: Props): JSX.Element {
    const cursorFunction = useObservableState(cursorFunction$);
    const [shiftPressed, setShiftPressed] = useState(false);

    useEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key !== SHIFT) {
            return;
        }

        if (cursorFunction$.getValue() === CursorFunction.SELECT) {
            return;
        }

        if (event.key === SHIFT) {
            setShiftPressed(true);
            cursorFunction$.next(CursorFunction.SELECT);
        }
    });

    useEventListener('keyup', (event: KeyboardEvent) => {
        if (event.key !== SHIFT) {
            return;
        }

        if (shiftPressed && cursorFunction$.getValue() === CursorFunction.SELECT) {
            cursorFunction$.next(CursorFunction.ZOOM);
        }

        setShiftPressed(false);
    });

    return (
        <>
            {Object.values(CursorFunction).map((name) => (
                <GraphToolbarButton
                    key={name}
                    onClick={() => cursorFunction$.next(name)}
                    active={cursorFunction === name}
                    ariaLabel={ariaTextMap[name]}
                >
                    <FontAwesomeIcon icon={iconMap[name]} />
                </GraphToolbarButton>
            ))}
        </>
    );
}
